import { computed, onMounted, onBeforeUnmount, watch, ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { tutorialService } from '../services/tutorialService'
import { tokenService } from '../services/tokenService'
import { log } from '../utils/DebugLogger'

export interface TutorialDefinition {
  slug: string
  status: 'not_started' | 'in_progress' | 'completed'
  [key: string]: any
}

export function useTutorial() {
  const route = useRoute()
  const i18n = (useI18n() as any)._i18n

  const availableTutorials: ComputedRef<TutorialDefinition[]> = computed(() => {
    return (tutorialService as any).getTutorialsForRoute(route.path)
  })

  const hasAvailableTutorials: ComputedRef<boolean> = computed(() => availableTutorials.value.length > 0)

  const isActive: ComputedRef<boolean> = computed(() => (tutorialService as any).state.activeTutorial !== null)

  let autoTriggerTimeout: ReturnType<typeof setTimeout> | null = null

  function autoTriggerForRoute(): void {
    if (autoTriggerTimeout) clearTimeout(autoTriggerTimeout)

    const tutorials: TutorialDefinition[] = (tutorialService as any).getTutorialsForRoute(route.path)
    const pending = tutorials.find((t) => t.status === 'not_started')

    if (pending) {
      autoTriggerTimeout = setTimeout(() => {
        log('useTutorial', 'autoTrigger', 'Starting tutorial', pending.slug)
        ;(tutorialService as any).startTutorial(pending.slug)
      }, 800)
    }
  }

  function replay(slug: string): void {
    log('useTutorial', 'replay', 'Replaying tutorial', slug)
    ;(tutorialService as any).startTutorial(slug, true)
  }

  function start(slug: string): void {
    ;(tutorialService as any).startTutorial(slug)
  }

  function stop(): void {
    ;(tutorialService as any).stopTutorial()
  }

  const stopRouteWatch = watch(
    () => route.path,
    () => {
      autoTriggerForRoute()
    }
  )

  onMounted(async () => {
    const tokenStatus = tokenService.getTokenStatus()
    if (tokenStatus === 'EXPIRED' || tokenStatus === 'INVALID') {
      log('useTutorial', 'onMounted', 'Skipping init — not authenticated')
      return
    }
    if (!(tutorialService as any).state.initialized) {
      await (tutorialService as any).init(i18n)
    }
    autoTriggerForRoute()
  })

  onBeforeUnmount(() => {
    if (autoTriggerTimeout) clearTimeout(autoTriggerTimeout)
    stopRouteWatch()
  })

  return {
    availableTutorials,
    hasAvailableTutorials,
    isActive,
    state: (tutorialService as any).state,
    replay,
    start,
    stop,
    registerTutorial: (tutorialService as any).registerTutorial
  }
}
